import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Chip,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Rating,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Alert,
  DialogContentText,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as CompletedIcon,
  LocalTaxi as TaxiIcon,
  DirectionsCar as CarIcon,
  Person as DriverIcon,
  PersonPin as CustomerIcon,
  Schedule as ScheduledIcon,
  Cancel as CancelledIcon,
  AccessTime as RequestedIcon,
  DirectionsRun as InProgressIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Room as DestinationIcon,
  Payments as PaymentIcon,
  Save as SaveIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  FlashOn as FlashOnIcon,
  ContentCopy as ContentCopyIcon,
  SwapHoriz as SwapHorizIcon,
  Phone as PhoneIcon,
  Smartphone as SmartphoneIcon,
  History as HistoryIcon,
  AssignmentTurnedIn as DispatchIcon,
  AutoAwesome as AutoFollowUpIcon,
  LocalHospital as MedicalIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import {
  fetchRideById,
  updateRideStatus,
  addRideFeedback,
  cancelRide,
  deleteRide,
  updateRide
} from '../../redux/slices/ridesSlice';
import { isCentralOperationsRole, isCentralRole } from '../../redux/slices/authSlice';
import { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { collection, addDoc, doc as firestoreDoc, getDoc } from 'firebase/firestore';
import { getVehicleTypeDescription, formatVehicleWithCapabilities, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import {
  BILLING_STATUS_OPTIONS,
  getOption
} from '../medicalTransportOrders/medicalTransportOrderOptions';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import MapView from '../common/MapView';

// Styled Components
const DetailCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const MEDICAL_PROOF_STATUS_OPTIONS = [
  { value: 'missing', label: 'Fehlt', color: 'warning' },
  { value: 'incomplete', label: 'Unvollständig', color: 'warning' },
  { value: 'complete', label: 'Vollständig', color: 'success' },
  { value: 'waived', label: 'Nicht möglich', color: 'info' },
  { value: 'problem', label: 'Problem', color: 'error' },
  { value: 'rejected', label: 'Beanstandet', color: 'error' }
];

const MEDICAL_PROOF_TYPE_LABEL_MAP = {
  digital_signature: 'Digitale Unterschrift',
  paper_proof: 'Papiernachweis',
  facility_confirmation: 'Einrichtung bestätigt',
  cannot_sign: 'Patient kann nicht unterschreiben',
  no_show: 'Patient nicht angetroffen',
  problem: 'Problem gemeldet'
};

const MEDICAL_PROOF_REASON_LABEL_MAP = {
  patient_cannot_sign: 'Patient kann nicht unterschreiben',
  patient_no_show: 'Patient nicht angetroffen',
  driver_reported_problem: 'Vom Fahrer gemeldetes Problem',
  signature_missing: 'Unterschrift fehlt'
};

const getDriverDisplayName = (driver) =>
  driver?.name || driver?.user?.name || driver?.displayName || driver?.email || null;

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  let icon;
  
  switch (status) {
    case 'completed':
      color = 'success';
      icon = <CompletedIcon />;
      break;
    case 'in_progress':
      color = 'primary';
      icon = <InProgressIcon />;
      break;
    case 'accepted':
    case 'pre_accepted': // pre_accepted ähnlich wie accepted
      color = 'info';
      icon = <DriverIcon />;
      break;
    case 'open':
    case 'dispatcher_pending':
      color = 'warning';
      icon = <RequestedIcon />;
      break;
    case 'arrived':
      color = 'arrived';
      icon = <DriverIcon />;
      break;
    case 'cancelled':
      color = 'error';
      icon = <CancelledIcon />;
      break;
    case 'cancelled_by_driver':
      color = 'error';
      icon = <CancelledIcon />;
      break;
    case 'scheduled':
      color = 'secondary';
      icon = <ScheduledIcon />;
      break;
    default:
      color = 'default';
      icon = <RequestedIcon />;
      break;
  }
  
  const backgroundColor =
    color === 'default'
      ? undefined
      : color === 'arrived'
        ? '#00897b'
        : theme.palette[color].main;

  return {
    color: color === 'default' ? 'inherit' : 'white',
    backgroundColor,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const LocationBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const RideDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { currentRide, isLoading, error } = useSelector(state => state.rides);
  const vehicles = useSelector(state => state.appData.vehicles);
  const drivers = useSelector(state => state.appData.drivers);
  const currentUser = useSelector(state => state.auth.user);
  const canDeleteRide = isCentralRole(currentUser);

  const driverById = useMemo(() => {
    if (!Array.isArray(drivers) || drivers.length === 0) return {};
    return drivers.reduce((map, driver) => {
      if (driver?.id) map[driver.id] = driver;
      return map;
    }, {});
  }, [drivers]);

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [gegenvorschlagDialogOpen, setGegenvorschlagDialogOpen] = useState(false);
  const [proposedTime, setProposedTime] = useState('');
  const [proposedNote, setProposedNote] = useState('');
  const [gegenvorschlagSaving, setGegenvorschlagSaving] = useState(false);
  const [medicalStatusSaving, setMedicalStatusSaving] = useState(false);
  
  useEffect(() => {
    if (id) {
      dispatch(fetchRideById(id));
    }
  }, [dispatch, id]);
  
  const handleFeedbackDialogOpen = () => {
    setRating(currentRide.rating || 0);
    setFeedback(currentRide.feedback || '');
    setFeedbackDialogOpen(true);
  };
  
  const handleFeedbackDialogClose = () => {
    setFeedbackDialogOpen(false);
  };
  
  const handleFeedbackSubmit = () => {
    dispatch(addRideFeedback({
      rideId: id,
      rating,
      feedback
    }));
    setFeedbackDialogOpen(false);
  };
  
  const handleStatusChange = (newStatus) => {
    dispatch(updateRideStatus({
      rideId: id,
      status: newStatus
    }));
  };

  const handleReleaseDialogOpen = () => {
    setReleaseDialogOpen(true);
  };

  const handleReleaseDialogClose = () => {
    setReleaseDialogOpen(false);
  };
  
  const handleReleaseRide = () => {
    handleReleaseDialogOpen();
  };
  
  const handleReleaseConfirm = () => {
    // Setze den Status zurück auf "scheduled"
    // und entferne die Fahrer- und Fahrzeugdetails
    dispatch(updateRideStatus({
      rideId: id,
      status: 'scheduled',
      additionalData: {
        driverId: null,
        driverName: null,
        driverProfilePic: null,
        driverRating: null,
        driverRatingCount: null,
        driverModel: null,
        driverLicensePlate: null,
        acceptedAt: null // Entferne den Akzeptierungszeitstempel
      }
    }));
    setReleaseDialogOpen(false);
  };
  
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteRide(id)).unwrap();
      setDeleteDialogOpen(false);
      navigate('/fahrten');
    } catch (error) {
      console.error('Fehler beim Löschen der Fahrt:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleSendGegenvorschlag = async () => {
    if (!proposedTime) return;
    setGegenvorschlagSaving(true);
    try {
      const proposedDate = new Date(proposedTime);
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);

      await dispatch(updateRide({
        rideId: id,
        gegenvorschlagTime: Timestamp.fromDate(proposedDate),
        gegenvorschlagCreatedAt: Timestamp.now(),
        gegenvorschlagExpiresAt: Timestamp.fromDate(expiresAt),
        gegenvorschlagStatus: 'pending',
        gegenvorschlagNote: proposedNote || null,
        originalScheduledTime: currentRide.scheduledTime || null,
      })).unwrap();

      // Push-Benachrichtigung an Kunden senden
      const customerId = currentRide.customerId || currentRide.customer_id;
      if (customerId) {
        const userRef = firestoreDoc(db, 'users', customerId);
        const userSnap = await getDoc(userRef);
        const fcmToken = userSnap.data()?.fcmToken;

        if (fcmToken) {
          const formattedTime = proposedDate.toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
          });
          await addDoc(collection(db, 'notifications'), {
            userId: customerId,
            title: 'Gegenvorschlag für Ihre Vorbestellung',
            body: `Wir schlagen eine alternative Abholzeit vor: ${formattedTime}. Bitte bestätigen oder stornieren Sie in der App.`,
            data: {
              type: 'gegenvorschlag',
              ride_id: id,
              action: 'new',
            },
            read: false,
            createdAt: Timestamp.now(),
            fcmToken: fcmToken,
          });
        }
      }

      setGegenvorschlagDialogOpen(false);
      setProposedTime('');
      setProposedNote('');
      dispatch(fetchRideById(id));
    } catch (error) {
      console.error('Fehler beim Senden des Gegenvorschlags:', error);
    } finally {
      setGegenvorschlagSaving(false);
    }
  };

  const handleMedicalStatusChange = async (field, value) => {
    if (!currentRide?.medicalTransportOrderId) return;
    setMedicalStatusSaving(true);
    try {
      await dispatch(updateRide({
        rideId: id,
        [field]: value
      })).unwrap();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Krankenfahrt-Status:', error);
    } finally {
      setMedicalStatusSaving(false);
    }
  };
  
  const formatDate = (dateInput) => {
    if (!dateInput) return 'Nicht angegeben';

    try {
      let date;

      // Firebase Timestamp mit .toDate() Methode
      if (dateInput.toDate) {
        date = dateInput.toDate();
      } else if (dateInput.seconds) {
        // Firebase Timestamp als Objekt mit .seconds Feld
        date = new Date(dateInput.seconds * 1000);
      } else {
        // String oder andere Formate
        date = new Date(dateInput);
      }

      // Prüfe, ob das Datum gültig ist
      if (isNaN(date.getTime())) {
        console.warn('Ungültiges Datum erhalten:', dateInput);
        return 'Nicht angegeben';
      }

      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Fehler beim Formatieren des Datums:', error, dateInput);
      return 'Nicht angegeben';
    }
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'Nicht angegeben';
    return `${parseFloat(value).toFixed(2)} €`;
  };
  
  const formatDistance = (value) => {
    if (!value && value !== 0) return 'Nicht angegeben';
    return `${parseFloat(value).toFixed(1)} km`;
  };
  
  const formatDuration = (value) => {
    if (!value && value !== 0) return 'Nicht angegeben';
    
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    
    if (hours > 0) {
      return `${hours} Std. ${minutes} Min.`;
    } else {
      return `${minutes} Min.`;
    }
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'accepted': 'Akzeptiert',
      'pre_accepted': 'Vom Fahrer angenommen',
      'in_progress': 'Zielfahrt',
      'arrived': 'Angekommen',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert',
      'cancelled_by_driver': 'Vom Fahrer storniert',
      'scheduled': 'Geplant',
      'open': 'Offen',
      'dispatcher_pending': 'Fahrer angefragt',
      'online': 'Online',
      'offline': 'Offline'
    };
    
    return statusMap[status] || status;
  };

  const getDispatchMethodLabel = (method) => {
    const map = {
      sectors: 'Nach Sektoren',
      distance: 'Entfernungsbasiert'
    };
    return map[method] || 'Nicht gesetzt';
  };

  const getDispatchSourceLabel = (source) => {
    const map = {
      sector_queue: 'Sektor-Warteschlange',
      distance_fallback: 'Entfernungs-Fallback',
      distance_direct: 'Entfernungsangebot',
      predicted_sector_followup: 'Automatische Anschlusstour'
    };
    return map[source] || source || 'Nicht gesetzt';
  };

  const getDispatchScopeLabel = (scope) => {
    const map = {
      central_pool: 'Zentraler Pool',
      operator_preferred: 'Unternehmer bevorzugt'
    };
    return map[scope] || scope || 'Nicht gesetzt';
  };

  const getOfferTypeLabel = (type) => {
    const map = {
      immediate: 'Sofortangebot',
      auto_follow_up: 'Anschlussangebot'
    };
    return map[type] || type || 'Nicht gesetzt';
  };

  const getDispatchStateLabel = (state) => {
    const map = {
      pending_offer: 'Angebot läuft',
      accepted: 'Angenommen',
      retrying_no_candidates: 'Suche läuft weiter',
      exhausted: 'Nicht vermittelbar',
      manual_required: 'Manuell erforderlich',
      dispatcher_assigned: 'Durch Zentrale zugewiesen',
      reset_to_distance: 'Auf Entfernung zurückgesetzt',
      pending_after_method_change: 'Wartet nach Methodenwechsel'
    };
    return map[state] || state || 'Nicht gesetzt';
  };

  const getDispatchReasonLabel = (reason) => {
    const map = {
      no_sector: 'Kein Pickup-Sektor gefunden',
      no_driver_in_sector: 'Kein freier Fahrer im Sektor',
      no_coordinates: 'Keine Koordinaten vorhanden',
      no_available_driver: 'Kein verfügbarer Fahrer',
      max_dispatch_offers_per_driver_reached: 'Alle verfügbaren Fahrer dreimal erfolglos angefragt',
      no_free_sector_driver_and_no_nearby_fallback: 'Kein Sektorfahrer und kein naher Fallback-Fahrer',
      sector_offer_expired: 'Sektor-Angebot abgelaufen',
      sector_offer_rejected: 'Sektor-Angebot abgelehnt',
      exclusive_offer_expired: 'Exklusivangebot abgelaufen',
      exclusive_offer_rejected: 'Exklusivangebot abgelehnt',
      dispatcher_direct_assignment: 'Direkte Zuweisung durch Zentrale'
    };
    return map[reason] || reason || 'Nicht gesetzt';
  };

  const getDispatchResultLabel = (result) => {
    const map = {
      accepted: 'Angenommen',
      rejected: 'Abgelehnt',
      expired: 'Abgelaufen',
      cancelled_method_changed: 'Durch Methodenwechsel abgebrochen',
      cancelled_dispatcher_assigned: 'Durch Zentrale übernommen'
    };
    return map[result] || result || 'Unbekannt';
  };

  const getDispatchResultColor = (result) => {
    if (result === 'accepted') return 'success';
    if (result === 'rejected') return 'warning';
    if (result === 'expired' || result === 'cancelled_method_changed') return 'default';
    return 'info';
  };

  const shortId = (value) => {
    if (!value || typeof value !== 'string') return 'Nicht gesetzt';
    return value.length > 10 ? `${value.substring(0, 10)}...` : value;
  };
  
  const getActiveStep = () => {
    const statusSteps = {
      'open': 0,
      'dispatcher_pending': 0,
      'accepted': 1,
      'arrived': 1, // arrived wird wie accepted behandelt (Stufe 1)
      'in_progress': 2,
      'completed': 3,
      'cancelled': -1
    };
    
    return statusSteps[currentRide?.status] || 0;
  };
  
  const canAcceptRide = () => {
    return currentRide && currentRide.status === 'dispatcher_pending';
  };

  const canActivatePreBooking = () => {
    return currentRide && currentRide.status === 'pre_accepted';
  };

  const canStartRide = () => {
    return currentRide && (currentRide.status === 'accepted' || currentRide.status === 'arrived');
  };
  
  const canCompleteRide = () => {
    return currentRide && (currentRide.status === 'in_progress' || currentRide.status === 'arrived');
  };
  
  const canCancelRide = () => {
    return currentRide && ['accepted', 'scheduled', 'open', 'arrived', 'dispatcher_pending'].includes(currentRide.status);
  };

  const isAutoAcceptedOpenPreOrder =
    currentRide?.orderType === 1 &&
    currentRide?.autoAccepted === true &&
    currentRide?.status === 'scheduled' &&
    !currentRide?.driverId &&
    !currentRide?.isPhoneCustomer;
  
  const canAddFeedback = () => {
    return isCentralRole(currentUser) && currentRide && currentRide.status === 'completed';
  };
  
  if (isLoading) {
    return <Loading message="Fahrt wird geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrt"
        message={error}
        onRetry={() => dispatch(fetchRideById(id))}
      />
    );
  }
  
  if (!currentRide) {
    return (
      <ErrorMessage
        title="Fahrt nicht gefunden"
        message="Die angeforderte Fahrt konnte nicht gefunden werden."
        onRetry={() => navigate('/fahrten')}
      />
    );
  }

  const dispatchHistory = Array.isArray(currentRide.dispatchOfferHistory)
    ? currentRide.dispatchOfferHistory
    : [];
  const hasDispatchInfo = Boolean(
    currentRide.dispatchMethod ||
    currentRide.dispatchState ||
    currentRide.dispatchOfferSource ||
    currentRide.dispatchScope ||
    currentRide.currentOfferDriverId ||
    currentRide.pickupSectorId ||
    currentRide.destinationSectorId ||
    currentRide.predictedFreeAt ||
    currentRide.autoFollowUpOffered ||
    dispatchHistory.length > 0
  );
  const medicalSnapshot = currentRide.medicalTransportSnapshot || {};
  const medicalProofStatus = currentRide.medicalTransportProofStatus || (currentRide.medicalTransportOrderId ? 'missing' : '');
  const medicalBillingStatus = currentRide.medicalTransportBillingStatus || (currentRide.medicalTransportOrderId ? 'not_ready' : '');
  const medicalProofOption = getOption(MEDICAL_PROOF_STATUS_OPTIONS, medicalProofStatus);
  const medicalBillingOption = getOption(BILLING_STATUS_OPTIONS, medicalBillingStatus);
  const medicalProofType = currentRide.medicalTransportProofType || '';
  const medicalProofTypeLabel = MEDICAL_PROOF_TYPE_LABEL_MAP[medicalProofType] || medicalProofType || 'Nicht erfasst';
  const medicalProofReasons = Array.isArray(currentRide.medicalTransportProofMissingReasons)
    ? currentRide.medicalTransportProofMissingReasons
    : [];
  const medicalSignaturePoints = Array.isArray(currentRide.medicalTransportSignaturePoints)
    ? currentRide.medicalTransportSignaturePoints
    : [];
  const hasMedicalSignature = medicalSignaturePoints.length > 0;
  const medicalProofNotes = currentRide.medicalTransportProofNotes || currentRide.billingNotes || '';
  const canEditRide = isCentralOperationsRole(currentUser) ||
    (currentUser?.operatorId && currentRide.createdByOperatorId === currentUser.operatorId);
  
  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="Fahrtdetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: `Fahrt ${id.substring(0, 8)}...`, path: `/fahrten/${id}` }
        ]}
        actionButton={canEditRide ? {
          label: 'Bearbeiten',
          icon: <EditIcon />,
          path: `/fahrten/${id}/bearbeiten`
        } : null}
      />
      
      {/* Fahrt Übersicht */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h1" sx={{ mr: 2 }}>
                  Fahrt #{id.substring(0, 8)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusChip 
                    label={translateStatus(currentRide.status)}
                    status={currentRide.status}
                    size="medium"
                    icon={
                      currentRide.status === 'completed' ? <CompletedIcon /> : 
                      currentRide.status === 'in_progress' ? <InProgressIcon /> : 
                      currentRide.status === 'accepted' ? <DriverIcon /> :
                      currentRide.status === 'pre_accepted' ? <DriverIcon /> :
                      currentRide.status === 'arrived' ? <DriverIcon /> :
                      currentRide.status === 'cancelled' ? <CancelledIcon /> :
                      currentRide.status === 'cancelled_by_driver' ? <CancelledIcon /> :
                      <ScheduledIcon />
                    }
                  />
                  <StyledBadge
                    badgeContent={currentRide.convertedToOpenRide ? "!" : 0}
                    color="error"
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
	                    <Chip
	                      icon={(currentRide.orderType === 1 || currentRide.convertedToOpenRide) ? <EventIcon /> : <FlashOnIcon />}
	                      label={(currentRide.orderType === 1 || currentRide.convertedToOpenRide) ? "Vorbestellung" : "Sofortbestellung"}
	                      color={(currentRide.orderType === 1 || currentRide.convertedToOpenRide) ? "secondary" : "primary"}
	                      variant="outlined"
	                    />
	                  </StyledBadge>
	                  {isAutoAcceptedOpenPreOrder && (
	                    <Tooltip title="Diese Vorbestellung wurde dem Kunden automatisch bestätigt, hat aber noch keinen Fahrer.">
	                      <Chip
	                        label="Automatisch bestätigt"
	                        color="success"
	                        variant="outlined"
	                      />
	                    </Tooltip>
	                  )}
	                </Box>
	              </Box>
              
	              {currentRide.convertedToOpenRide && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{ mb: 2 }}
                >
                  Diese Vorbestellung wurde am {formatDate(currentRide.convertedAt)} automatisch in eine Sofortbestellung umgewandelt,
                  da kein Fahrer die Fahrt angenommen hat.
                </Alert>
	              )}

	              {isAutoAcceptedOpenPreOrder && (
	                <Alert severity="success" sx={{ mb: 2 }}>
	                  Diese Vorbestellung wurde dem Kunden automatisch bestätigt. Es ist noch kein Fahrer zugewiesen.
	                </Alert>
	              )}

              {currentRide.orderType === 1 && currentRide.status === 'pre_accepted' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {currentRide.dispatchOpenAt ? (
                    <>
                      Online-Prüfung ab <strong>{formatDate(currentRide.dispatchOpenAt)}</strong>: NextCab prüft,
                      ob Fahrer {currentRide.driverName || 'Unbekannt'} online ist. Bei externen Abholungen richtet
                      sich dieser Zeitpunkt nach der Google-Maps-Fahrzeit. Ist der Fahrer offline, wird die
                      Vorbestellung automatisch wieder zur Disposition freigegeben.
                    </>
                  ) : (
                    <>
                      Online-Prüfung: Ab dem berechneten Freigabezeitpunkt prüft NextCab, ob Fahrer{' '}
                      {currentRide.driverName || 'Unbekannt'} online ist. Bei externen Abholungen kann diese Prüfung
                      entsprechend der Google-Maps-Fahrzeit früher erfolgen. Ist der Fahrer offline, wird die
                      Vorbestellung automatisch wieder zur Disposition freigegeben.
                    </>
                  )}
                </Alert>
              )}

              {currentRide.gegenvorschlagStatus && (
                <Alert
                  severity={
                    currentRide.gegenvorschlagStatus === 'pending' ? 'warning' :
                    currentRide.gegenvorschlagStatus === 'accepted' ? 'success' :
                    currentRide.gegenvorschlagStatus === 'declined' ? 'error' : 'info'
                  }
                  icon={<SwapHorizIcon />}
                  sx={{ mb: 2 }}
                >
                  <strong>Gegenvorschlag: </strong>
                  {currentRide.gegenvorschlagStatus === 'pending' && (
                    <>Vorgeschlagene Zeit: {formatDate(currentRide.gegenvorschlagTime)} — Warte auf Antwort des Kunden (läuft ab am {formatDate(currentRide.gegenvorschlagExpiresAt)})</>
                  )}
                  {currentRide.gegenvorschlagStatus === 'accepted' && (
                    <>Vom Kunden akzeptiert. Neue Abholzeit: {formatDate(currentRide.gegenvorschlagTime)}</>
                  )}
                  {currentRide.gegenvorschlagStatus === 'declined' && (
                    <>Vom Kunden abgelehnt. Die Fahrt wurde storniert.</>
                  )}
                  {currentRide.gegenvorschlagStatus === 'expired' && (
                    <>Abgelaufen ohne Antwort. Die Fahrt wurde storniert.</>
                  )}
                  {currentRide.gegenvorschlagNote && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>Notiz: {currentRide.gegenvorschlagNote}</Typography>
                  )}
                </Alert>
              )}
              
              <LocationBox>
                <LocationIcon />
                <Typography variant="body1">
                  <strong>Abholadresse:</strong> {currentRide.start}
                </Typography>
              </LocationBox>
              
              <LocationBox>
                <DestinationIcon />
                <Typography variant="body1">
                  <strong>Zieladresse:</strong> {currentRide.destination || 'Kein Ziel angegeben'}
                </Typography>
              </LocationBox>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Bestelltyp:</strong> {currentRide.orderType === 1 ? 'Vorbestellung' : 'Sofortbestellung'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Fahrzeugtyp:</strong> {formatVehicleWithCapabilities(currentRide.vehicleType, currentRide.specialCapabilities || migrateSpecialVehicleType(currentRide))}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={currentRide.isPhoneCustomer ? <PhoneIcon /> : <SmartphoneIcon />}
                    label={currentRide.isPhoneCustomer ? "Telefonkunde" : "App-Kunde"}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: currentRide.isPhoneCustomer ? 'warning.main' : 'success.main',
                      color: currentRide.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                      '& .MuiChip-icon': {
                        color: currentRide.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                      },
                    }}
                  />
                  {currentRide.isKrankenfahrt && (
                    <Chip label="Krankenfahrt" size="small" color="error" variant="outlined" />
                  )}
                  {currentRide.isRechnungsfahrt && (
                    <Chip label="Rechnungsfahrt" size="small" color="info" variant="outlined" />
                  )}
                  {currentRide.isAnschlusstour && (
                    <Chip label="Anschlusstour" size="small" color="success" variant="outlined" />
                  )}
                </Box>
                {currentRide.notes && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <strong>Notizen:</strong> {currentRide.notes}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  <strong>Anfragezeitpunkt:</strong> {formatDate(currentRide.timestamp || currentRide.request_time)}
                </Typography>
                {currentRide.scheduledTime && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Geplant für:</strong> {formatDate(currentRide.scheduledTime)}
                  </Typography>
                )}
                {(currentRide.acceptedTimestamp || currentRide.acceptedAt) && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Angenommen am:</strong> {formatDate(currentRide.acceptedTimestamp || currentRide.acceptedAt)}
                  </Typography>
                )}
                {currentRide.startedAt && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Fahrt begonnen:</strong> {formatDate(currentRide.startedAt)}
                  </Typography>
                )}
                {currentRide.arrivedTimestamp && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Ankunft am Abholort:</strong> {formatDate(currentRide.arrivedTimestamp)}
                  </Typography>
                )}
                {currentRide.passengerPickedUpTimestamp && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Passagier abgeholt:</strong> {formatDate(currentRide.passengerPickedUpTimestamp)}
                  </Typography>
                )}
                {(currentRide.completedTimestamp || currentRide.completionTimestamp) && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Abschlusszeitpunkt:</strong> {formatDate(currentRide.completedTimestamp || currentRide.completionTimestamp)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Zahlungsinformationen
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body1">
                  <strong>{currentRide.isFestpreis ? 'Festpreis:' : 'Fahrpreis ca.:'}</strong> {formatCurrency(currentRide.estimatedEarningEur || currentRide.fare)}
                </Typography>
                <Typography variant="body1">
                  <strong>Zahlungsmethoden:</strong> {
                    currentRide.paymentMethods === 1 ? 'Nur Barzahlung' :
                    currentRide.paymentMethods === 2 ? 'Bar- und Kartenzahlung' :
                    currentRide.paymentMethods === 3 ? 'Bar-, Karten- und PayPal-Zahlung' :
                    'Nicht angegeben'
                  }
                </Typography>
              </Box>
              
              {currentRide.rating && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Bewertung:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Rating 
                      value={currentRide.rating} 
                      readOnly 
                      precision={0.5}
                      icon={<StarIcon fontSize="inherit" />}
                    />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      ({currentRide.rating})
                    </Typography>
                  </Box>
                  {currentRide.feedback && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      "{currentRide.feedback}"
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' } }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 2,
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'stretch', md: 'flex-end' },
                  '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
                }}
              >
                {canAcceptRide() && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<CompletedIcon />}
                    onClick={() => handleStatusChange('accepted')}
                  >
                    Fahrt akzeptieren
                  </Button>
                )}

                {canActivatePreBooking() && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<StartIcon />}
                    onClick={() => handleStatusChange('accepted')}
                  >
                    Fahrt starten
                  </Button>
                )}

                {canStartRide() && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<StartIcon />}
                    onClick={() => handleStatusChange('in_progress')}
                  >
                    Fahrgast zugestiegen
                  </Button>
                )}
                
                {canCompleteRide() && (
                  <Button 
                    variant="contained" 
                    color="success"
                    startIcon={<EndIcon />}
                    onClick={() => handleStatusChange('completed')}
                  >
                    Fahrt abschließen
                  </Button>
                )}
                
                {canCancelRide() && (
                  <Button 
                    variant="outlined" 
                    color="error"
                    startIcon={<CancelledIcon />}
                    onClick={() => handleStatusChange('cancelled')}
                  >
                    Stornieren
                  </Button>
                )}
                
                {/* Neuer Button: Fahrt zurücknehmen für Vorbestellungen mit Status pre_accepted */}
                {currentRide.orderType === 1 && currentRide.status === 'pre_accepted' && (
                  <Button 
                    variant="outlined" 
                    color="warning"
                    startIcon={<CancelledIcon />}
                    onClick={() => handleReleaseRide()}
                  >
                    Fahrt zurücknehmen
                  </Button>
                )}
                
                {canAddFeedback() && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<StarIcon />}
                    onClick={handleFeedbackDialogOpen}
                  >
                    {currentRide.rating ? 'Bewertung bearbeiten' : 'Bewertung hinzufügen'}
                  </Button>
                )}

                {[
                  'completed', 'cancelled', 'cancelled_by_driver',
                  'open', 'dispatcher_pending', 'accepted', 'pre_accepted',
                  'arrived', 'in_progress', 'scheduled'
                ].includes(currentRide.status) && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => navigate('/fahrten/neu', { state: { duplicateRide: currentRide } })}
                  >
                    {['completed', 'cancelled', 'cancelled_by_driver'].includes(currentRide.status)
                      ? 'Erneut erstellen'
                      : '2. Taxi anfordern'}
                  </Button>
                )}
              </Box>
              
              
              {['scheduled', 'open', 'dispatcher_pending', 'pre_accepted', 'accepted'].includes(currentRide.status) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                  {['pre_accepted', 'accepted'].includes(currentRide.status) && (
                    <Alert severity="warning" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                      {currentRide.status === 'accepted'
                        ? `Fahrer ${currentRide.driverName || 'Unbekannt'} hat die Fahrt bereits angenommen. Eine Neuzuweisung entzieht ihm die Fahrt.`
                        : `Fahrt ist Fahrer ${currentRide.driverName || 'Unbekannt'} zugewiesen. Eine Neuzuweisung entzieht ihm die Fahrt.`}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    color={['pre_accepted', 'accepted'].includes(currentRide.status) ? 'warning' : 'primary'}
                    component={Link}
                    to={`/fahrten/${id}/zuweisen`}
                    startIcon={<TaxiIcon />}
                  >
                    {currentRide.driverId ? 'Fahrer neu zuweisen' : 'Fahrer zuweisen'}
                  </Button>
                  {(!currentRide.gegenvorschlagStatus || currentRide.gegenvorschlagStatus === 'expired') && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<SwapHorizIcon />}
                      onClick={() => setGegenvorschlagDialogOpen(true)}
                    >
                      Gegenvorschlag senden
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {(currentRide.isKrankenfahrt || currentRide.medicalTransportOrderId) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <MedicalIcon color="error" />
            <Typography variant="h6">Krankenfahrt / Verordnung</Typography>
          </Box>

          {currentRide.medicalTransportOrderId ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2">
                  <strong>Patient:</strong> {medicalSnapshot.patientName || currentRide.customerName || 'Nicht gesetzt'}
                </Typography>
                <Typography variant="body2">
                  <strong>Krankenkasse:</strong> {medicalSnapshot.healthInsuranceName || 'Nicht gesetzt'}
                </Typography>
                <Typography variant="body2">
                  <strong>Verordnungsnummer:</strong> {medicalSnapshot.prescriptionNumber || 'Nicht gesetzt'}
                </Typography>
                <Typography variant="body2">
                  <strong>Genehmigungsnummer:</strong> {medicalSnapshot.approvalNumber || 'Nicht gesetzt'}
                </Typography>
                <Button
                  component={Link}
                  to={`/krankenfahrten/${currentRide.medicalTransportOrderId}`}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Verordnung öffnen
                </Button>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="medical-proof-status-label">Nachweisstatus</InputLabel>
                      <Select
                        labelId="medical-proof-status-label"
                        value={medicalProofStatus}
                        label="Nachweisstatus"
                        disabled={medicalStatusSaving}
                        onChange={(event) => handleMedicalStatusChange('medicalTransportProofStatus', event.target.value)}
                      >
                        {MEDICAL_PROOF_STATUS_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Chip
                      sx={{ mt: 1 }}
                      size="small"
                      label={medicalProofOption.label}
                      color={medicalProofOption.color}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="medical-billing-status-label">Abrechnungsstatus</InputLabel>
                      <Select
                        labelId="medical-billing-status-label"
                        value={medicalBillingStatus}
                        label="Abrechnungsstatus"
                        disabled={medicalStatusSaving}
                        onChange={(event) => handleMedicalStatusChange('medicalTransportBillingStatus', event.target.value)}
                      >
                        {BILLING_STATUS_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Chip
                      sx={{ mt: 1 }}
                      size="small"
                      label={medicalBillingOption.label}
                      color={medicalBillingOption.color}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                {currentRide.status === 'completed' && medicalProofStatus === 'complete' && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Nachweis vollständig und Fahrt abgeschlossen. Die Fahrt ist für die Abrechnung bereit.
                  </Alert>
                )}
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Nachweisdetails
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2">
                        <strong>Nachweisart:</strong> {medicalProofTypeLabel}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2">
                        <strong>Erfasst am:</strong> {formatDate(currentRide.medicalTransportProofCapturedAt)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2">
                        <strong>Digitale Signatur:</strong> {hasMedicalSignature ? `Vorhanden (${medicalSignaturePoints.length} Punkte)` : 'Nicht vorhanden'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2">
                        <strong>No-Show:</strong> {currentRide.noShow ? 'Ja' : 'Nein'}
                      </Typography>
                    </Grid>
                    {medicalProofReasons.length > 0 && (
                      <Grid size={12}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Gründe:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {medicalProofReasons.map(reason => (
                            <Chip
                              key={reason}
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={MEDICAL_PROOF_REASON_LABEL_MAP[reason] || reason}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                    {medicalProofNotes && (
                      <Grid size={12}>
                        <Typography variant="body2">
                          <strong>Notiz:</strong> {medicalProofNotes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="warning">
              Diese Fahrt ist als Krankenfahrt markiert, aber noch keiner Verordnung zugeordnet.
            </Alert>
          )}
        </Paper>
      )}

      {hasDispatchInfo && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DispatchIcon color="primary" />
            <Typography variant="h6">Disposition</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Methode:</strong> {getDispatchMethodLabel(currentRide.dispatchMethod)}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {getDispatchStateLabel(currentRide.dispatchState)}
                </Typography>
                <Typography variant="body2">
                  <strong>Quelle:</strong> {getDispatchSourceLabel(currentRide.dispatchOfferSource)}
                </Typography>
                <Typography variant="body2">
                  <strong>Sichtbarkeit:</strong> {getDispatchScopeLabel(currentRide.dispatchScope)}
                </Typography>
                <Typography variant="body2">
                  <strong>Angebotstyp:</strong> {getOfferTypeLabel(currentRide.currentOfferType)}
                </Typography>
                <Typography variant="body2">
                  <strong>Grund:</strong> {getDispatchReasonLabel(currentRide.dispatchFallbackReason || currentRide.autoFollowUpReason)}
                </Typography>
                <Typography variant="body2">
                  <strong>Aktueller Angebotsempfänger:</strong> {shortId(currentRide.currentOfferDriverId)}
                </Typography>
                {currentRide.currentOfferExpiresAt && (
                  <Typography variant="body2">
                    <strong>Angebot läuft ab:</strong> {formatDate(currentRide.currentOfferExpiresAt)}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Pickup-Sektor:</strong>{' '}
                  {currentRide.pickupSectorName || currentRide.pickupSectorCode || shortId(currentRide.pickupSectorId)}
                </Typography>
                <Typography variant="body2">
                  <strong>Zielsektor:</strong>{' '}
                  {currentRide.destinationSectorName || currentRide.destinationSectorCode || shortId(currentRide.destinationSectorId)}
                </Typography>
                <Typography variant="body2">
                  <strong>Vorhergesagter Frei-Sektor:</strong>{' '}
                  {currentRide.predictedFreeSectorName || currentRide.predictedFreeSectorCode || shortId(currentRide.predictedFreeSectorId)}
                </Typography>
                {currentRide.predictedFreeAt && (
                  <Typography variant="body2">
                    <strong>Voraussichtlich frei:</strong> {formatDate(currentRide.predictedFreeAt)}
                    {currentRide.predictedFreeMinutes ? ` (${currentRide.predictedFreeMinutes} Min.)` : ''}
                  </Typography>
                )}
                {currentRide.autoFollowUpOffered && (
                  <Alert severity="info" icon={<AutoFollowUpIcon />} sx={{ mt: 1 }}>
                    Diese Fahrt wurde als automatische Anschlusstour angeboten.
                    {currentRide.autoFollowUpActiveRideId && (
                      <> Aktive Vorfahrt: {shortId(currentRide.autoFollowUpActiveRideId)}</>
                    )}
                  </Alert>
                )}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <HistoryIcon color="action" fontSize="small" />
            <Typography variant="subtitle2">Dispatch-Historie</Typography>
          </Box>

          {dispatchHistory.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {dispatchHistory.slice().reverse().map((entry, index) => (
                <Box
                  key={`${entry.driverId || 'driver'}-${entry.endedAt?.seconds || index}-${index}`}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={getDispatchResultLabel(entry.result)}
                      color={getDispatchResultColor(entry.result)}
                      variant={entry.result === 'accepted' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      size="small"
                      label={getDispatchSourceLabel(entry.source)}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={getOfferTypeLabel(entry.offerType)}
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2">
                    <strong>Fahrer:</strong> {getDriverDisplayName(driverById[entry.driverId]) || shortId(entry.driverId)}
                    {entry.sectorId && <> · <strong>Sektor:</strong> {shortId(entry.sectorId)}</>}
                    {entry.activeRideId && <> · <strong>Aktive Fahrt:</strong> {shortId(entry.activeRideId)}</>}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Start: {formatDate(entry.startedAt)} · Ende: {formatDate(entry.endedAt)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Noch keine Dispatch-Historie vorhanden.
            </Typography>
          )}
        </Paper>
      )}
      
      {/* KPIs/Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CustomerIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Kunde</Typography>
              </Box>
              <Typography variant="body1">{currentRide.customer?.name || currentRide.customerName || 'Nicht angegeben'}</Typography>
              <Typography variant="body2">{currentRide.customer?.email || 'Keine E-Mail'}</Typography>
              <Typography variant="body2">{currentRide.customer?.phone || currentRide.customerPhone || 'Keine Telefonnummer'}</Typography>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DriverIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrer</Typography>
              </Box>
              {currentRide.driver ? (
                <>
                  <Typography variant="body1">{currentRide.driver.name || currentRide.driverName || 'Unbekannt'}</Typography>
                  <Typography variant="body2">
                    Status: {translateStatus(currentRide.driver.isOnline ? 'online' : 'offline')}
                  </Typography>
                  <Typography variant="body2">
                    Tel.: {currentRide.driver.phone || 'Nicht angegeben'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1">Kein Fahrer zugewiesen</Typography>
              )}
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrzeug</Typography>
              </Box>
              {(() => {
                const fallbackVehicle = currentRide.driverId
                  ? vehicles?.find(v => (v.driverId || v.current_driver?.id) === currentRide.driverId)
                  : null;
                const vModel = currentRide.vehicle?.model || currentRide.driverModel || fallbackVehicle?.model;
                const vPlate = currentRide.vehicle?.licensePlate || currentRide.driverLicensePlate || fallbackVehicle?.licensePlate;
                return (vModel || vPlate) ? (
                <>
                  <Typography variant="body1">{vModel || 'Unbekannt'}</Typography>
                  <Typography variant="body2">
                    Kennzeichen: {vPlate || 'Nicht angegeben'}
                  </Typography>
                  <Typography variant="body2">
                    Fahrzeugtyp: {formatVehicleWithCapabilities(currentRide.vehicleType, currentRide.specialCapabilities || migrateSpecialVehicleType(currentRide))}
                  </Typography>
                  {currentRide.vehicle?.id && (
                    <Box sx={{ mt: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        component={Link}
                        to={`/fahrzeuge/${currentRide.vehicle.id}`}
                      >
                        Fahrzeug anzeigen
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1">Kein Fahrzeug zugewiesen</Typography>
              );
              })()}
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Details</Typography>
              </Box>
              <Typography variant="body1">
                <strong>Entfernung:</strong> {formatDistance(currentRide.distanceKm)}
              </Typography>
              <Typography variant="body1">
                <strong>Dauer:</strong> {formatDuration(currentRide.duration || currentRide.estimatedTimeMinutes)}
              </Typography>
              <Typography variant="body1">
                <strong>{currentRide.isFestpreis ? 'Festpreis:' : 'Fahrpreis ca.:'}</strong> {formatCurrency(currentRide.estimatedEarningEur || currentRide.fare)}
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
      </Grid>
      
      {/* Kartenansicht */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Strecke
        </Typography>
        <Box>
          <MapView
            pickupLocation={
              currentRide.startLatitude && currentRide.startLongitude
                ? { lat: currentRide.startLatitude, lng: currentRide.startLongitude }
                : null
            }
            destinationLocation={
              currentRide.destinationLatitude && currentRide.destinationLongitude && currentRide.destination
                ? { lat: currentRide.destinationLatitude, lng: currentRide.destinationLongitude }
                : null
            }
            pickupAddress={currentRide.start}
            destinationAddress={currentRide.destination}
            currentLocation={currentRide.driver?.location}
            height={400}
            showDirections={true}
            showInfoWindows={true}
          />
        </Box>
      </Paper>
      
      {/* Fahrt-Verlauf */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Fahrtverlauf
        </Typography>
        <Box sx={{ maxWidth: 600 }}>
          <Stepper activeStep={getActiveStep()} orientation="vertical">
            <Step>
              <StepLabel>Fahrt angefragt</StepLabel>
              <StepContent>
                <Typography>
                  Fahrt wurde am {formatDate(currentRide.timestamp)} angefragt.
                  {currentRide.scheduledTime && ` Geplant für ${formatDate(currentRide.scheduledTime)}.`}
                </Typography>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Fahrer zugewiesen</StepLabel>
              <StepContent>
                {currentRide.driver ? (
                  <Typography>
                    Fahrer {currentRide.driver.name || currentRide.driverName || 'Unbekannt'} wurde am {formatDate(currentRide.acceptedAt || currentRide.acceptedTimestamp)} zugewiesen.
                  </Typography>
                ) : (
                  <Typography>
                    Warten auf Fahrerzuweisung...
                  </Typography>
                )}
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Fahrt in Bearbeitung</StepLabel>
              <StepContent>
                {currentRide.passengerPickedUpTimestamp || currentRide.startedAt ? (
                  <Typography>
                    Fahrt wurde am {formatDate(currentRide.passengerPickedUpTimestamp || currentRide.startedAt)} gestartet.
                  </Typography>
                ) : (
                  <Typography>
                    Warten auf Abholung...
                  </Typography>
                )}
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Fahrt abgeschlossen</StepLabel>
              <StepContent>
                {(currentRide.completedTimestamp || currentRide.completionTimestamp) ? (
                  <Typography>
                    Fahrt wurde am {formatDate(currentRide.completedTimestamp || currentRide.completionTimestamp)} abgeschlossen.
                    {currentRide.rating && ` Mit ${currentRide.rating} Sternen bewertet.`}
                  </Typography>
                ) : (
                  <Typography>
                    Warten auf Abschluss...
                  </Typography>
                )}
              </StepContent>
            </Step>
          </Stepper>
          
          {(currentRide.status === 'cancelled' || currentRide.status === 'cancelled_by_driver') && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Diese Fahrt wurde am {formatDate(currentRide.cancelledTimestamp || currentRide.cancelledAt || currentRide.timestamp)} storniert.
              {currentRide.cancelReason && ` Grund: ${currentRide.cancelReason}`}
            </Alert>
          )}

          {currentRide.cancellationHistory && currentRide.cancellationHistory.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Stornierungsverlauf
              </Typography>
              {currentRide.cancellationHistory.map((entry, index) => (
                <Alert severity="warning" sx={{ mb: 1 }} key={index}>
                  <strong>{entry.driverName}</strong>: {entry.reason}
                  {entry.timestamp && (
                    <>
                      <br />
                      <Typography variant="caption" component="span">
                        {formatDate(entry.timestamp)}
                      </Typography>
                    </>
                  )}
                </Alert>
              ))}
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Bewertung Dialog */}
      <Dialog open={feedbackDialogOpen} onClose={handleFeedbackDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bewertung {currentRide.rating ? 'bearbeiten' : 'hinzufügen'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Wie würden Sie diese Fahrt bewerten?
              </Typography>
              <Rating 
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue);
                }}
                precision={0.5}
                size="large"
              />
            </Box>
            <TextField
              fullWidth
              margin="normal"
              label="Feedback"
              multiline
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFeedbackDialogClose}>Abbrechen</Button>
          <Button 
            onClick={handleFeedbackSubmit} 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!rating}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog zum Bestätigen der Fahrtfreigabe */}
      <Dialog open={releaseDialogOpen} onClose={handleReleaseDialogClose} maxWidth="sm">
        <DialogTitle>Fahrt zurücknehmen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Möchten Sie die Vorbestellung wirklich zurücknehmen?
            </Typography>
            <Typography variant="body2" color="error">
              Hinweis: Der Fahrer {currentRide?.driverName} wird dadurch von dieser Fahrt abgemeldet.
              Der Status wird wieder auf "Geplant" gesetzt.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReleaseDialogClose}>Abbrechen</Button>
          <Button 
            onClick={handleReleaseConfirm} 
            variant="contained" 
            color="warning"
          >
            Fahrt zurücknehmen
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Aktionsbuttons */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {canDeleteRide && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            Fahrt löschen
          </Button>
        )}
        {/* ... other buttons ... */}
      </Box>

      {/* Lösch-Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Fahrt löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie diese Fahrt endgültig löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gegenvorschlag Dialog */}
      <Dialog open={gegenvorschlagDialogOpen} onClose={() => setGegenvorschlagDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gegenvorschlag senden</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Der Kunde wird per Push-Benachrichtigung über den Gegenvorschlag informiert
            und hat <strong>24 Stunden</strong> Zeit zu reagieren. Ohne Reaktion wird die Fahrt automatisch storniert.
          </Alert>
          {currentRide?.scheduledTime && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Ursprüngliche Zeit:</strong> {formatDate(currentRide.scheduledTime)}
            </Typography>
          )}
          <TextField
            label="Vorgeschlagene Abholzeit"
            type="datetime-local"
            value={proposedTime}
            onChange={(e) => setProposedTime(e.target.value)}
            fullWidth
            required
            sx={{ mb: 2 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Begründung (optional)"
            value={proposedNote}
            onChange={(e) => setProposedNote(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="z.B. Zu dieser Zeit sind leider keine Fahrer verfügbar..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGegenvorschlagDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSendGegenvorschlag}
            disabled={!proposedTime || gegenvorschlagSaving}
            startIcon={<SwapHorizIcon />}
          >
            {gegenvorschlagSaving ? 'Senden...' : 'Gegenvorschlag senden'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RideDetails;
